import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import ToggleTabGroup from '../components/atoms/ToggleTabGroup';
import { DeleteDialog, PageHeader } from '../components/partials';
import AppsList from '../components/partials/VerticalList/AppsList';
import { Button, Dialog, EmptyState, Shelf, Spinner } from '../components/widgets';
import PermissionsWrapper from '../containers/PermissionsWrapper';
import { MANAGE_LINKS_EVENT_PREFIX } from '../utils/general';
import LinkGroupContainers from './components/LinkGroupContainers';
import TopArea from './components/TopArea';
import { TOGGLE_TAB_GROUP_OPTIONS } from './consts';
import LinkEditorShelf from './containers/LinkEditorShelf';
import css from './page.css';
import SubDomainsPage from './components/manageDomainsShelf/SubDomainsPage';

const FAQ_LINKS = { faqLink: 'https://support.singular.net/hc/en-us/articles/360030934212-Singular-Links-FAQ' };

const LinkManagement = props => {
    const {
        loading = true,
        loadingLinks = true,
        searching = true,
        onAppSelected = () => {},
        groups = [],
        onAppFilterChanged = () => {},
        appsFilter = '',
        onExpandClick = () => {},
        onCreateLinkClicked = () => {},
        onEditClick = () => {},
        onDuplicateClicked = () => {},
        shelf = { open: false },
        onTopButtonClicked = () => {},
        subDomainsPage = { open: false },
        emptyState = {},
        onEmptyStateAction = () => {},
        oldLinksCount = 0,
        agenciesLinksCount = 0,
        apiLinksCount = 0,
        translate,
        selectedToggleTab = 'ALL_LINKS',
        onToggleTabChanged = () => {},
        allLinksCount = 0,
        standardLinksCount = 0,
        showApiToggle = false,
        showApiLinkCount = true,
        apps = [],
        onCloseShelf = () => {},
        onCloseSubDomainsShelf = () => {},
        onArchiveClicked = () => {},
    } = props;

    const [shelfFormDirty, setShelfFormDirty] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [openGroups, setOpenGroups] = useState({
        custom: false,
    });

    const archiveLinkDataRef = useRef(null);

    const onFormDirtyChanged = useCallback(dirty => {
        setShelfFormDirty(dirty);
    }, []);

    const handleArchiveLink = useCallback((linkId, singularLink, campaignId) => {
        archiveLinkDataRef.current = {
            singularLink,
            linkId,
            campaignId,
        };
        setShowDeleteDialog(true);
    }, []);

    const handleArchiveLinkAccept = useCallback(() => {
        if (archiveLinkDataRef.current) {
            onArchiveClicked(
                archiveLinkDataRef.current.linkId,
                archiveLinkDataRef.current.singularLink,
                archiveLinkDataRef.current.campaignId
            );
        }
        setShowDeleteDialog(false);
        archiveLinkDataRef.current = null;
    }, [onArchiveClicked]);

    const handleArchiveLinkReject = useCallback(() => {
        setShowDeleteDialog(false);
        archiveLinkDataRef.current = null;
    }, []);

    const handleGroupTitleClicked = useCallback(groupName => {
        setOpenGroups(prev => {
            if (!(groupName in prev)) {
                return {
                    ...prev,
                    [groupName]: false,
                };
            }
            return {
                ...prev,
                [groupName]: !prev[groupName],
            };
        });
    }, []);

    const isGroupOpen = useCallback(
        group => {
            return (!(group.name in openGroups) && group.open) || openGroups[group.name];
        },
        [openGroups]
    );

    const getToggleCountByValue = useCallback(count => {
        if (count === null) {
            return 'N/A';
        }
        if (count < 10000) {
            return count;
        }
        return '9999+';
    }, []);

    const getAppListWithContent = useCallback(() => {
        return apps.map(app => ({
            ...app,
            lastUpdate: app.lastUpdate,
            content: app.content ? (
                <div key={app.id} className={css.appSummaryContent}>
                    <Translate
                        id="STATIC.PAGES.MANAGE_LINKS.CUSTOM_LINKS_COUNT"
                        data={{ customCount: app.content.customCount }}
                    />
                    <br />
                    <Translate
                        id="STATIC.PAGES.MANAGE_LINKS.PARTNER_LINKS_COUNT"
                        data={{ partnerCount: app.content.partnerCount }}
                    />
                </div>
            ) : (
                <></>
            ),
        }));
    }, [apps]);

    const onCloseShelfHandler = useCallback(() => {
        if (shelfFormDirty) {
            if (!confirm(translate('STATIC.PAGES.CUSTOM_EVENTS.UNSAVED_CHANGES_WARNING'))) {
                return;
            }
        }
        setShelfFormDirty(false);
        archiveLinkDataRef.current = null;
        onCloseShelf();
    }, [shelfFormDirty, translate, onCloseShelf]);

    const toggleTabGroupOptions = useMemo(() => {
        return [
            {
                option: TOGGLE_TAB_GROUP_OPTIONS.ALL_LINKS,
                label: translate('STATIC.PAGES.MANAGE_LINKS.ALL_LINKS_TOGGLE_LABEL', {
                    count: getToggleCountByValue(allLinksCount),
                }),
                ...(apiLinksCount > 0 && { tooltip: translate('STATIC.PAGES.MANAGE_LINKS.ALL_LINKS_TOGGLE_TOOLTIP') }),
            },
            ...(allLinksCount !== standardLinksCount && standardLinksCount > 0
                ? [
                      {
                          option: TOGGLE_TAB_GROUP_OPTIONS.STANDARD_LINKS,
                          label: translate('STATIC.PAGES.MANAGE_LINKS.STANDARD_LINKS_TOGGLE_LABEL', {
                              count: getToggleCountByValue(standardLinksCount),
                          }),
                      },
                  ]
                : []),
            ...(agenciesLinksCount > 0
                ? [
                      {
                          option: TOGGLE_TAB_GROUP_OPTIONS.AGENCY_LINKS,
                          label: translate('STATIC.PAGES.MANAGE_LINKS.AGENCY_LINKS_TOGGLE_LABEL', {
                              count: getToggleCountByValue(agenciesLinksCount),
                          }),
                      },
                  ]
                : []),
            ...(oldLinksCount > 0
                ? [
                      {
                          option: TOGGLE_TAB_GROUP_OPTIONS.OLD_LINKS,
                          label: translate('STATIC.PAGES.MANAGE_LINKS.OLD_LINKS_TOGGLE_LABEL', {
                              count: getToggleCountByValue(oldLinksCount),
                          }),
                          tooltip: translate('STATIC.PAGES.MANAGE_LINKS.LEGACY_TOGGLE_TOOLTIP'),
                          disabled: emptyState?.oldLinksDisabled,
                      },
                  ]
                : []),
            ...(showApiToggle
                ? [
                      {
                          option: TOGGLE_TAB_GROUP_OPTIONS.API_LINKS,
                          label: translate('STATIC.PAGES.MANAGE_LINKS.API_LINKS_TOGGLE_LABEL', {
                              count: showApiLinkCount ? getToggleCountByValue(apiLinksCount) : '...',
                          }),
                      },
                  ]
                : []),
        ];
    }, [
        translate,
        allLinksCount,
        apiLinksCount,
        agenciesLinksCount,
        oldLinksCount,
        standardLinksCount,
        emptyState,
        showApiToggle,
        showApiLinkCount,
        getToggleCountByValue,
    ]);

    if (subDomainsPage?.open) {
        return <SubDomainsPage {...subDomainsPage} />;
    }

    if (loading) {
        return <Spinner show expanded />;
    }

    const showToggleTabGroup =
        toggleTabGroupOptions.length > 2 ||
        (toggleTabGroupOptions.length === 2 &&
            allLinksCount !== Math.max(apiLinksCount, agenciesLinksCount, oldLinksCount)) ||
        showApiToggle;

    return (
        <div className={css.container}>
            <div className={css.topArea}>
                <TopArea
                    onAppFilterChanged={onAppFilterChanged}
                    search={appsFilter}
                    onClick={onTopButtonClicked}
                    emptyState={emptyState}
                    searching={searching}
                />
            </div>
            <div className={css.mainArea}>
                <AppsList
                    sortApps={e => e}
                    className={css.leftArea}
                    apps={getAppListWithContent()}
                    appsFilter={appsFilter}
                    onAppSelected={onAppSelected}
                    disableAnimation
                />
                <div className={css.rightArea}>
                    <div className={css.rightAreaTop}>
                        <PageHeader
                            subText={FAQ_LINKS}
                            text="STATIC.PAGES.MANAGE_LINKS.AVAILABLE_LINKS"
                            mixpanelEventPrefix={MANAGE_LINKS_EVENT_PREFIX}
                        />
                        <div className={css.actions}>
                            <PermissionsWrapper write>
                                <Button onClick={onCreateLinkClicked} disabled={emptyState?.createLinkDisabled}>
                                    <Translate id="STATIC.PAGES.MANAGE_LINKS.CREATE_LINK_BUTTON" />
                                </Button>
                            </PermissionsWrapper>
                            {showToggleTabGroup && (
                                <ToggleTabGroup
                                    options={toggleTabGroupOptions}
                                    selectedOption={selectedToggleTab}
                                    onToggle={onToggleTabChanged}
                                />
                            )}
                        </div>
                    </div>
                    <div className={css.rightAreaMain}>
                        {loadingLinks && <Spinner show style={{ marginTop: 120 }} />}
                        {!loadingLinks && !emptyState && (
                            <LinkGroupContainers
                                groups={groups}
                                isGroupOpen={isGroupOpen}
                                onTitleClick={handleGroupTitleClicked}
                                handleArchiveLink={handleArchiveLink}
                                onEditClick={onEditClick}
                                onDuplicateClicked={onDuplicateClicked}
                                onExpandClick={onExpandClick}
                            />
                        )}
                        {!loadingLinks && emptyState && (
                            <EmptyState
                                style={{ marginTop: 120, textAlign: 'center' }}
                                {...emptyState.data}
                                onActionTriggered={onEmptyStateAction}
                            />
                        )}
                    </div>
                </div>
            </div>
            <Shelf {...shelf} shelfSize="medium" onClose={onCloseShelfHandler}>
                <LinkEditorShelf onFormDirtyChanged={onFormDirtyChanged} />
            </Shelf>
            <Dialog open={showDeleteDialog} onEscapePress={handleArchiveLinkReject}>
                <DeleteDialog
                    title="STATIC.PAGES.VIEW_TRACKING_LINKS.DELETE_WARNING"
                    acceptText="STATIC.PAGES.VIEW_TRACKING_LINKS.ARCHIVE_YES"
                    onAccept={handleArchiveLinkAccept}
                    onReject={handleArchiveLinkReject}
                    deleteType="link"
                />
            </Dialog>
        </div>
    );
};

LinkManagement.propTypes = {
    loading: PropTypes.bool,
    loadingLinks: PropTypes.bool,
    searching: PropTypes.bool,
    apps: PropTypes.arrayOf(PropTypes.any),
    onAppSelected: PropTypes.func,
    onAppFilterChanged: PropTypes.func,
    onExpandClick: PropTypes.func,
    onCreateLinkClicked: PropTypes.func,
    onCloseShelf: PropTypes.func,
    onCloseSubDomainsShelf: PropTypes.func,
    onEditClick: PropTypes.func,
    appsFilter: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.any),
    shelf: PropTypes.objectOf(PropTypes.any),
    onTopButtonClicked: PropTypes.func,
    subDomainsPage: PropTypes.objectOf(PropTypes.any),
    onDuplicateClicked: PropTypes.func,
    onArchiveClicked: PropTypes.func,
    onEmptyStateAction: PropTypes.func,
    allLinksCount: PropTypes.number,
    standardLinksCount: PropTypes.number,
    oldLinksCount: PropTypes.number,
    agenciesLinksCount: PropTypes.number,
    apiLinksCount: PropTypes.number,
    translate: PropTypes.func,
    emptyState: PropTypes.objectOf(PropTypes.any),
    selectedToggleTab: PropTypes.string,
    onToggleTabChanged: PropTypes.func,
    showApiToggle: PropTypes.bool,
    showApiLinkCount: PropTypes.bool,
};

export default withLocalize(LinkManagement);
